/**
 * Approval workflow functions for Fehlzeiten (Absence) service layer.
 * 
 * Handles all approval, review, and status change operations.
 * NO direct Supabase imports should exist in UI components.
 * 
 * @module fehlzeiten/services/approval
 */

import { supabase } from '../../../../lib/supabase';
import type {
  UpdateNoteActionParams,
  ReviewLatenessParams,
  ReviewLatenessByDateParams,
  GetOrCreateNoteParams,
  GetOrCreateNoteResult,
  ServiceResult,
} from '../types/services';
import { normalizeTime } from './helpers';

// ============================================================================
// Absence Note Management
// ============================================================================

/**
 * Gets an existing absence note or creates a new one for the student/date.
 * 
 * This is the primary function for ensuring an absence note exists before
 * applying approval actions.
 * 
 * @param params - Parameters for finding or creating the note
 * @returns Result with note ID and whether it was newly created
 * 
 * @example
 * const result = await getOrCreateAbsenceNote({
 *   studentId: 'uuid',
 *   date: '2026-01-15',
 *   status: 'krankgemeldet',
 *   startTime: '09:00',
 *   endTime: '12:00',
 * });
 */
export async function getOrCreateAbsenceNote(
  params: GetOrCreateNoteParams
): Promise<ServiceResult<GetOrCreateNoteResult>> {
  const { studentId, date, status, reason, startTime, endTime, createdBy } = params;

  const { data, error } = await supabase.rpc('fn_get_or_create_absence_note' as never, {
    p_student_id: studentId,
    p_date: date,
    p_status: status || 'unentschuldigt',
    p_reason: reason || null,
    p_created_by: createdBy || null,
    p_start_time: normalizeTime(startTime),
    p_end_time: normalizeTime(endTime),
  } as never);

  if (error) {
    return { success: false, error: error.message };
  }

  const rows = Array.isArray(data) ? data : [data];
  if (!rows.length || !rows[0]) {
    return { success: false, error: 'No note returned from RPC' };
  }

  const row = rows[0] as { note_id: string; is_new?: boolean };
  return {
    success: true,
    data: {
      noteId: row.note_id,
      isNew: !!row.is_new,
    },
  };
}

// ============================================================================
// Approval Actions
// ============================================================================

/**
 * Updates the approval action on an absence note.
 * 
 * Used for actions like: attest, schriftliche_entschuldigung, genehmigt, etc.
 * 
 * @param params - Note ID and action to apply
 * @returns Service result indicating success or failure
 * 
 * @example
 * await updateNoteAction({
 *   noteId: 'uuid',
 *   action: 'attest',
 *   performedBy: 'user-uuid',
 * });
 */
export async function updateNoteAction(
  params: UpdateNoteActionParams
): Promise<ServiceResult> {
  const { noteId, action, performedBy, reason } = params;

  // RPC signature: p_note_id, p_action, p_by, p_at, p_reason
  const { error } = await supabase.rpc('update_absence_note_action' as never, {
    p_note_id: noteId,
    p_action: action,
    p_by: performedBy || null,
    p_reason: reason || null,
  } as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Sets the status on an absence note.
 * 
 * @param noteId - Absence note UUID
 * @param status - New status to set
 * @param performedBy - User ID performing the action
 * @returns Service result
 */
export async function setNoteStatus(
  noteId: string,
  status: string,
  performedBy?: string
): Promise<ServiceResult> {
  // RPC signature: p_note_id, p_new_status, p_by, p_at
  const { error } = await supabase.rpc('fn_fehlzeiten_set_note_status' as never, {
    p_note_id: noteId,
    p_new_status: status,
    p_by: performedBy || null,
  } as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Updates an absence slice (per-lesson approval).
 * 
 * @param params - Note ID, lesson ID, and approval settings
 * @returns Service result
 */
export async function updateAbsenceSlice(params: {
  noteId: string;
  lessonId: string;
  action: string;
  isExcused?: boolean | null;
  performedBy?: string | null;
  reason?: string | null;
}): Promise<ServiceResult> {
  // RPC signature: p_note_id, p_lesson_id, p_action, p_excused, p_by, p_at, p_reason
  const { error } = await supabase.rpc('update_absence_slice_react' as never, {
    p_note_id: params.noteId,
    p_lesson_id: params.lessonId,
    p_action: params.action,
    p_excused: params.isExcused ?? null,
    p_by: params.performedBy || null,
    p_reason: params.reason || null,
  } as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

// ============================================================================
// Lateness Review
// ============================================================================

/**
 * Reviews lateness for a specific lesson.
 * 
 * @param params - Student ID, lesson ID, and whether excused
 * @returns Service result
 * 
 * @example
 * await reviewLatenessPerLesson({
 *   studentId: 'uuid',
 *   lessonId: 'lesson-uuid',
 *   isExcused: true,
 *   reviewedBy: 'teacher-uuid',
 *   action: 'attest',
 * });
 */
export async function reviewLatenessPerLesson(
  params: ReviewLatenessParams
): Promise<ServiceResult> {
  const { studentId, lessonId, isExcused, reviewedBy, action } = params;

  // RPC signature: p_student_id, p_lesson_id, p_excused, p_by, p_action
  const { error } = await supabase.rpc('review_lateness_per_lesson' as never, {
    p_student_id: studentId,
    p_lesson_id: lessonId,
    p_excused: isExcused,
    p_by: reviewedBy || null,
    p_action: action || null,
  } as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Reviews lateness for all lessons on a specific date.
 * 
 * @param params - Student ID, date, and whether excused
 * @returns Service result
 */
export async function reviewLatenessByDate(
  params: ReviewLatenessByDateParams
): Promise<ServiceResult> {
  const { studentId, date, isExcused, reviewedBy, action } = params;

  // RPC signature: p_student_id, p_date, p_excused, p_by, p_action
  const { error } = await supabase.rpc('review_lateness_by_student_date' as never, {
    p_student_id: studentId,
    p_date: date,
    p_excused: isExcused,
    p_by: reviewedBy || null,
    p_action: action || null,
  } as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Clears lateness review for a specific lesson.
 * 
 * @param studentId - Student UUID
 * @param lessonId - Lesson UUID
 * @returns Service result
 */
export async function clearLatenessPerLesson(
  studentId: string,
  lessonId: string
): Promise<ServiceResult> {
  // RPC signature: p_student_id, p_lesson_id
  const { error } = await supabase.rpc('clear_lateness_per_lesson' as never, {
    p_student_id: studentId,
    p_lesson_id: lessonId,
  } as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

// ============================================================================
// Legacy Approval Functions
// ============================================================================

/**
 * Sets beurlaubt (leave) approval status.
 * 
 * @deprecated Use updateNoteAction with appropriate action instead
 * @param noteId - Absence note UUID
 * @param approved - Whether the leave is approved
 * @param action - Specific action (befuerwortet, genehmigt)
 * @returns Service result
 */
export async function setBeurlaubtApproval(
  noteId: string,
  approved: boolean,
  action?: string
): Promise<ServiceResult> {
  const { error } = await supabase
    .from('student_absence_notes' as never)
    .update({
      approval_state: approved ? 'geprueft' : 'abgelehnt',
      approval_action: action || (approved ? 'beurlaubt_genehmigt' : null),
    } as never)
    .eq('id' as never, noteId as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Cleans up orphaned absence notes.
 * 
 * @internal Maintenance function, should not be called from UI
 * @returns Service result
 */
export async function cleanupOrphanedAbsenceNotes(): Promise<ServiceResult> {
  const { error } = await supabase.rpc('fn_cleanup_orphaned_absence_notes' as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

// ============================================================================
// Mass Update Functions
// ============================================================================

export interface MassUpdateEntry {
  studentId: string;
  date: string;
  noteId?: string | null;
}

export interface MassUpdateParams {
  entries: MassUpdateEntry[];
  newStatus?: string | null;
  reason?: string;
  approvalAction?: string | null;
  fillAttendance?: boolean;
  performedBy?: string | null;
  attendanceStartTime?: string;
  attendanceEndTime?: string;
}

export interface MassUpdateResult {
  success: boolean;
  updatedCount: number;
  createdCount: number;
  excludedCount: number;
  excludedStudents: Array<{
    student_id: string;
    student_name: string;
    date: string;
    reason: string;
  }>;
  errorMessage?: string;
}

/**
 * Mass creates or updates absence notes.
 * 
 * Creates absence notes if they don't exist, updates them if they do.
 * Excludes students who:
 * - Were marked present on that date
 * - Were on a class excursion on that date
 * 
 * @param params - Mass update parameters with student_id + date entries
 * @returns Result with counts and excluded student details
 * 
 * @example
 * const result = await massCreateOrUpdateAbsences({
 *   entries: [
 *     { studentId: 'uuid1', date: '2025-10-08' },
 *     { studentId: 'uuid2', date: '2025-10-08' },
 *   ],
 *   newStatus: 'beurlaubt',
 *   reason: 'Heizungsausfall',
 *   approvalAction: 'beurlaubt_genehmigt',
 *   fillAttendance: true,
 * });
 */
export async function massCreateOrUpdateAbsences(
  params: MassUpdateParams
): Promise<MassUpdateResult> {
  const { entries, newStatus, reason, approvalAction, fillAttendance, performedBy, attendanceStartTime, attendanceEndTime } = params;

  if (!entries.length) {
    return {
      success: true,
      updatedCount: 0,
      createdCount: 0,
      excludedCount: 0,
      excludedStudents: [],
    };
  }

  // Convert entries to the format expected by the RPC (JSONB array)
  const entriesJson = entries.map(e => ({
    student_id: e.studentId,
    date: e.date,
    note_id: e.noteId || null,
  }));

  console.log('[massCreateOrUpdateAbsences] Calling RPC with', entriesJson.length, 'entries');

  const { data, error } = await supabase.rpc('fn_mass_create_or_update_absences' as never, {
    p_entries: entriesJson,
    p_new_status: newStatus || 'krankgemeldet',
    p_reason: reason || null,
    p_approval_action: approvalAction || null,
    p_fill_attendance: fillAttendance || false,
    p_performed_by: performedBy || null,
    p_attendance_start_time: attendanceStartTime || null,
    p_attendance_end_time: attendanceEndTime || null,
  } as never);

  if (error) {
    console.error('[massCreateOrUpdateAbsences] RPC error:', error);
    return {
      success: false,
      updatedCount: 0,
      createdCount: 0,
      excludedCount: 0,
      excludedStudents: [],
      errorMessage: error.message,
    };
  }

  const rows = Array.isArray(data) ? data : [data];
  const row = rows[0] as {
    success: boolean;
    updated_count: number;
    created_count: number;
    excluded_count: number;
    excluded_students: Array<{
      student_id: string;
      student_name: string;
      date: string;
      reason: string;
    }>;
    error_message: string | null;
  } | undefined;

  if (!row || !row.success) {
    return {
      success: false,
      updatedCount: 0,
      createdCount: 0,
      excludedCount: 0,
      excludedStudents: [],
      errorMessage: row?.error_message || 'Unknown error from RPC',
    };
  }

  return {
    success: true,
    updatedCount: row.updated_count,
    createdCount: row.created_count,
    excludedCount: row.excluded_count,
    excludedStudents: row.excluded_students || [],
  };
}
